<script setup lang="ts">
import { resolveComponent } from 'vue';
import type { THEME } from '@pkg/config/misc/theme';
import { ALIGNMENT } from '@pkg/sanity-utils/constants/alignment';
import { type Image } from '@sanity/types';
import { cva } from 'class-variance-authority';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import { useResolvedSectionTheme } from '@pkg/modular-blocks/frontend/useResolvedSectionTheme';
import SectionImages from './SectionImages.vue';

const sectionVariants = cva(
  'c-section-block',
  {
    variants: {
      variant: {
        noImage: 'c-section-block--no-image',
        imageLeft: 'c-section-block--image-left',
        imageRight: 'c-section-block--image-right',
      },
      extraSpacing: {
        true: 'c-section-block--extra-spacing',
      },
      alignment: {
        [ALIGNMENT.LEFT]: 'c-section-block--align-left',
        [ALIGNMENT.CENTER]: 'c-section-block--align-center',
      },
    },
  },
);

type Props = {
  theme: THEME;
  anchor?: string;
  loading: 'lazy' | 'eager';
  variant?: NonNullable<Parameters<typeof sectionVariants>[0]>['variant'];
  extraSpacing?: boolean;
  content?: Array<ModularBlock>,
  image?: Image;
  subImage?: Image;
  alignment?: NonNullable<Parameters<typeof sectionVariants>[0]>['alignment'];
}

const props = withDefaults(defineProps<Props>(), {
  anchor: undefined,
  extraSpacing: false,
  variant: 'noImage',
  image: undefined,
  subImage: undefined,
  content: () => [],
  alignment: ALIGNMENT.LEFT,
  loading: 'lazy',
});

const resolvedTheme = useResolvedSectionTheme(() => props.theme);

/** Host app registers PartialSection (web + business). */
const PartialSection = resolveComponent('PartialSection');
</script>

<template>
  <component
    :is="PartialSection"
    :anchor="anchor"
    :theme="resolvedTheme"
    :class="sectionVariants({ extraSpacing, variant, alignment })"
  >
    <div class="c-section-block__wrapper wrapper">
      <div class="c-section-block__content c-section-block__content--inner content-container">
        <slot>
          <ModularBlocksRenderer :blocks="content || []" />
        </slot>
      </div>
      <SectionImages
        v-if="variant !== 'noImage' && image != null"
        :loading="loading"
        :image="image"
        :sub-image="subImage"
        :alignment="variant === 'imageLeft' ? 'left' : 'right'"
      />
    </div>
  </component>
</template>

<style scoped>

.c-section-block {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @screen tablet-wide {
    gap: 2rem;
  }

  &__wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  @screen tablet-wide {
    &--align-left {
      & .c-section-block__content {
        text-align: left;
      }
    }

    &--align-center {
      & .c-section-block__content {
        align-items: center;
        text-align: center;
      }
    }
  }

  &__content {
    flex-direction: column;
    display: flex;
    flex: 1;
    align-items: center;
    text-align: center;
  }

  &--image-left, &--image-right {
    & .c-section-block__wrapper {
      flex-direction: column-reverse;
      align-items: center;
      gap: 1.5rem;

      @screen tablet-wide {
        flex-direction: row;
      }
    }

    & .c-section-block__content--inner {
      width: 100%;
      @screen tablet-wide {
        max-width: 588px;
      }
    }

    & .c-section-block__image {
      --image-size: 320px;
      max-height: var(--image-size);
      max-width: var(--image-size);
      width: var(--image-size);
      height: var(--image-size);

      object-fit: cover;

      @screen tablet-wide {
        --image-size: 486px;
      }

    }
  }

  &--image-left {
    & .c-section-block__wrapper {
      @screen tablet-wide {
        flex-direction: row-reverse;
      }
    }
  }

  &--extra-spacing {
    & .c-section-block__wrapper {
      margin-top: theme('margin.120');
      margin-bottom: theme('margin.120');
    }
  }
}

</style>
